import { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { AUTH_STORAGE_KEYS, decodeJwtPayload, tokenService } from '@aimsos/auth';
import {
  Button,
  FormSelect,
  WidgetGrid,
  WidgetGridProvider,
  defaultDataAdapters,
  useDashboardList,
} from '@aimsos/ui';

import { EmptyState, ErrorState, LoadingState } from '../components/states/index.js';
import { fetchDashboardLayout, fetchDashboardList } from '../../../services/dashboards.js';
import { fetchWidgetData } from '../../../services/widgetData.js';

import { LayoutDashboard, Pencil } from 'lucide-react';

/**
 * Overview tab — the dashboard published for this entity type, fed with the
 * data of the record the user opened.
 *
 * A CONSUMPTION surface, not an authoring one. Widgets can be dragged and
 * resized; there is no way to add or remove one, and Edit redirects to the
 * Widget Builder rather than editing in place.
 *
 * Nothing here reimplements the renderer. `WidgetGrid` ships the grid, the drag
 * layer, the widget card shell, the skeletons and the per-widget error
 * boundary — so one failing widget cannot blank the tab.
 */
function DashboardTabContent({ tab }) {
  const { t } = useTranslation('unified-profiles');
  const navigate = useNavigate();
  const { entityType, entityId, tabActionsSlot } = useOutletContext();
  // The module comes from the TAB, not a constant: Garage and Appointments are
  // the same renderer on a different module.
  const module = tab?.module ?? '';

  // The platform's user id, NOT `useAuth().user.id` — that object is the Auth0
  // profile (`sub`, `name`, `email`) and has no `id`, so reaching for one
  // yields `undefined` on every render.
  //
  // The authoritative source is the `user_uuid` claim on our own JWT. The
  // localStorage mirror is only a fallback: it is absent on a real logged-in
  // session (verified in the browser — the key simply is not there, which is
  // also why the shell's own `userId` is empty), so reading it alone would
  // reproduce the very bug this replaced.
  //
  // Getting it wrong is silent: `useWidgetGrid` persists layout at
  // `wg_${userId}_${dashboardUuid}`, so an empty id gives everyone on a shared
  // browser one another's layout.
  const userId = useMemo(() => {
    const claims = decodeJwtPayload(tokenService.getToken());
    return claims?.user_uuid ?? localStorage.getItem(AUTH_STORAGE_KEYS.USER_UUID) ?? '';
  }, []);

  // `defaultDataAdapters` are the package's Storybook mocks: they know nothing
  // about our entities and cannot produce 403/404. Spread for forward
  // compatibility, then override all three with the real ones.
  const dataAdapters = useMemo(
    () => ({
      ...defaultDataAdapters,
      fetchDashboardList,
      fetchDashboardLayout,
      fetchWidgetData,
    }),
    [],
  );

  const {
    dashboards,
    selectedUuid,
    selectDashboard,
    loading,
    error: dashboardListError,
    refetch,
  } = useDashboardList({
    // The entity type from the route — never a hardcoded 'CONTACT', even though
    // the hook's own JSDoc uses that as its example.
    scopeType: entityType,
    module,
    fetchDashboardList: dataAdapters.fetchDashboardList,
  });

  // `DashboardSelector` (the package's own wrapper) hardcodes `FormSelect`
  // with no `className`/size passthrough, so its trigger is stuck at the
  // fixed 2.75rem `min-h-[2.75rem]` baked into `FormSelect`'s own default
  // class string. `FormSelect` itself DOES take `triggerClassName` — so this
  // calls it directly rather than through the wrapper, the same
  // plain-`!`-utility technique already used for `PreviewSizeControl`'s S/M/L
  // rail (`packages/ui` fights same-specificity conflicts by stylesheet
  // order, not by class order in the string — a bare override loses unless
  // it's marked `!important`). No `packages/ui` file changes either way.
  const dashboardOptions = useMemo(
    () => dashboards.map((d) => ({ label: d.name, value: d.dashboard_uuid })),
    [dashboards],
  );
  const handleSelectDashboard = useCallback(
    (uuid) => {
      if (uuid && uuid !== selectedUuid) selectDashboard(uuid);
    },
    [selectDashboard, selectedUuid],
  );

  // `useDashboardList` returns the raw Error, where this domain's own hooks
  // return a message string. Handing the object straight to a `subtitle` makes
  // React throw on "Objects are not valid as a React child" — the tab renders
  // nothing at all instead of the error state, which is how a forced failure
  // showed up as a blank panel rather than a retry.
  const error = useMemo(() => {
    if (!dashboardListError) return null;
    return dashboardListError.message ?? String(dashboardListError);
  }, [dashboardListError]);

  const scopeContext = useMemo(
    () => ({ entity_type: entityType, entity_id: entityId }),
    [entityType, entityId],
  );

  // Cross-app destination: the Widget Builder lives in @aimsos/dashboards, so
  // this is a shell-level route rather than a relative one. The single
  // deliberate absolute path in this domain.
  const handleEdit = useCallback(() => navigate('/widgets'), [navigate]);

  const body = () => {
    if (loading) return <LoadingState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (dashboards.length === 0) {
      return (
        <EmptyState
          icon={LayoutDashboard}
          title={t('overview.empty.title')}
          subtitle={t('overview.empty.subtitle')}
        />
      );
    }

    return (
      <WidgetGridProvider
        scopeType={entityType}
        module={module}
        scopeContext={scopeContext}
        userId={userId}
        dataAdapters={dataAdapters}
        isEditMode
      >
        {/* The negative margin below widens what `WidgetGrid` measures as its
            own container, which also widens it past this page's real edges by
            the same 28px on each side — and the scroll wrapper this sits
            inside only sets `overflow-y-auto`, so per the CSS overflow spec
            the other axis computes to `auto` too: that 28px became real,
            scrollable horizontal overflow (confirmed via `scrollWidth` >
            `clientWidth` in the browser), not just extra layout space nobody
            could reach. This wrapper clips it back to the page's own width —
            clipping is fine here because what's clipped is exactly the
            reserved-but-empty drag-handle gutter, never a widget's content. */}
        <div className="overflow-hidden">
          <div
            data-testid="overview-grid"
            data-scope-type={entityType}
            data-scope-entity={entityId}
            // `WidgetGrid` reserves `GRID_MARGIN[0] + DRAG_HANDLE_OFFSET_PX`
            // (16 + 12 = 28px, from `packages/ui`'s own constants — not
            // ours to change) on BOTH sides so a widget's drag handle never
            // clips against the edge. That reads as misaligned padding
            // against the card above it, which reserves none. Countered here,
            // at the call site, rather than in the shared component: the
            // negative margin widens what `WidgetGrid` measures as its own
            // container by exactly the amount it insets, so the cards land
            // back at this page's real edges instead of 28px short of them.
            className="-mx-[28px] min-w-0 flex-1"
          >
            {/* `onDashboardRetry` defaults to `window.location.reload()` inside
                the package. A full reload inside the shell throws away the
                user's place, so it is overridden with a real retry. */}
            <WidgetGrid dashboardUuid={selectedUuid} onDashboardRetry={refetch} />
          </div>
        </div>
      </WidgetGridProvider>
    );
  };

  // Hides itself at one dashboard — nothing to switch to. The ZERO case is
  // different and is handled below as an empty state, because "no dashboards
  // you can access" is not the same thing as "one dashboard, no choice to
  // make".
  //
  // Portals into the shell's tab row (`UnifiedProfilePage`) rather than
  // rendering its own row here, so it sits space-between with the tabs
  // instead of stacking a second toolbar row underneath them. Falls back to
  // rendering inline on the rare tick before the shell's ref callback has
  // fired — never nothing.
  const toolbar = (
    <>
      <div
        data-testid="dashboard-selector"
        className={dashboards.length > 1 ? 'min-w-[200px] max-w-[320px]' : undefined}
      >
        {dashboards.length > 1 ? (
          <FormSelect
            value={selectedUuid || ''}
            onChange={handleSelectDashboard}
            options={dashboardOptions}
            placeholder={t('overview.selectDashboard')}
            disabled={loading}
            triggerClassName="!min-h-0 !h-[30px] !py-1"
            portalListbox
          />
        ) : null}
      </div>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={Pencil}
        onClick={handleEdit}
        className="!h-[30px]"
      >
        {t('overview.edit')}
      </Button>
    </>
  );

  return (
    <div
      data-testid="overview-tab"
      data-entity-id={entityId}
      className="flex min-w-0 flex-1 flex-col gap-4 pb-6"
    >
      {tabActionsSlot ? (
        createPortal(toolbar, tabActionsSlot)
      ) : (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{toolbar}</div>
      )}
      {/* No scroller of its own: the profile page owns the only one (CT-5526),
          and a nested one would leave its sticky header pinned against a
          container that never moves. */}
      <div className="flex min-w-0 flex-1 flex-col">{body()}</div>
    </div>
  );
}

export default DashboardTabContent;
